import type { FormikErrors } from 'formik';
import { Form, Formik } from 'formik';
import type { ReactElement } from 'react';
import { useState } from 'react';
import z, { ZodError } from 'zod';
import type { AccountPaymentsSuccessResponse } from '@libs/common-next';
import { BANNERS, logSentryError, SentryAcpCreditCardsErrors as SentryErrors } from '@libs/common-next';
import type { AddressFormTypeUSandCA } from '../AddressForm';
import { BillingAddress } from '../BillingAddress';
import type { ExperianResponseLayout } from '../ExperianModal';
import { ExperianModal, validateExperianAddressLayout } from '../ExperianModal';
import { CardDetailsInputs } from './CardDetailsInputs';
import { IsDefaultCheckbox } from './IsDefaultCheckbox';
import type { EditPaymentFormInterface } from './schema';
import { editPaymentFormSchema } from './schema';
import { formatCreditCardExpirationDate, updatePayment, validateAddressForm } from './utils';
const scrollToFirstErrorField = (errorKey: string) => {
  const field = document.querySelector<HTMLElement>(`[name="${errorKey}"]`);
  if (field && typeof field.scrollIntoView === 'function') {
    field.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }
  field?.focus();
};
const DEFAULT_EDIT_PAYMENT_FORM_STATE: EditPaymentFormInterface = {
  addressType: 'id',
  nameOnCard: '',
  expirationDate: '',
  isPrimary: false,
  selectedAddressId: '',
  cardId: 0
};
export function EditPaymentForm({
  isDefaultCheckDisabled,
  onCloseForm,
  onSuccess,
  onError,
  defaultAddressId,
  editPaymentData,
  onSubmit,
  onAddressValidationApiError,
  onExperianModalVisibilityChange = () => {},
  addAddressIconSize,
  onClearError
}: Readonly<{
  isDefaultCheckDisabled: boolean;
  editPaymentData: AccountPaymentsSuccessResponse;
  onCloseForm: () => void;
  onSuccess: (isDefault: boolean) => Promise<void>;
  onError?: (error: unknown) => void;
  defaultAddressId?: string;
  onSubmit?: (values: EditPaymentFormInterface) => Promise<void>;
  onAddressValidationApiError?: () => void;
  onExperianModalVisibilityChange?: (isOpen: boolean) => void;
  disableUntilDirty?: boolean;
  addAddressIconSize?: 'sm' | 'md' | 'lg';
  onClearError?: () => void;
}>): ReactElement {
  const [isExperianLoading, setIsExperianLoading] = useState(false);
  const [experianResponse, setExperianResponse] = useState<ExperianResponseLayout | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const paymentFormState: EditPaymentFormInterface = {
    ...DEFAULT_EDIT_PAYMENT_FORM_STATE,
    addressType: 'id',
    selectedAddressId: editPaymentData.addressId?.toString() ?? '',
    nameOnCard: editPaymentData.fullName,
    expirationDate: formatCreditCardExpirationDate(editPaymentData.expiration),
    isPrimary: editPaymentData.isPrimary,
    cardId: editPaymentData.cardId
  };
  const validate = async (values: EditPaymentFormInterface) => {
    let errors: FormikErrors<EditPaymentFormInterface> = {};
    try {
      editPaymentFormSchema.parse(values);
    } catch (error) {
      if (error instanceof ZodError) {
        errors = z.flattenError(error).fieldErrors;
      }
    }
    if (values.address) {
      const addressErrors = await validateAddressForm(values.address);
      if (addressErrors) {
        errors.address = addressErrors as FormikErrors<AddressFormTypeUSandCA>;
      }
    }
    return Object.keys(errors) ? errors : true;
  };
  const handleErrorBanner = (errors_0?: FormikErrors<EditPaymentFormInterface>): boolean => {
    const hasFormErrors = errors_0 ? Object.keys(errors_0).length > 0 : false;
    if (hasFormErrors && errors_0) {
      const isMissingAddressOnly = Object.keys(errors_0).length === 1 && 'selectedAddressId' in errors_0;
      const errorMessage = isMissingAddressOnly ? 'Please select or add a billing address' : BANNERS.invalidPaymentMethodFormik.text ?? '';
      onError?.(errorMessage);
      scrollToFirstErrorField(Object.keys(errors_0)[0]);
      return false;
    }
    onClearError?.();
    return true;
  };
  const handleSubmitForm = async (values_0: EditPaymentFormInterface): Promise<void> => {
    onClearError?.();
    setIsSubmitting(true);
    if (onSubmit) {
      await onSubmit(values_0);
      setIsSubmitting(false);
      return;
    }
    try {
      const response = await updatePayment(values_0);
      if (response && 'cardId' in response) {
        await onSuccess?.(response.isPrimary);
      } else {
        throw new Error('Failed to update payment method');
      }
    } catch (error_0) {
      logSentryError(error_0, SentryErrors.EditPaymentForm.SubmitFailed);
      onError?.(error_0);
    } finally {
      setIsSubmitting(false);
    }
  };
  const save = async (values_1: EditPaymentFormInterface) => {
    try {
      await handleSubmitForm(values_1);
    } finally {
      setIsSubmitting(false);
    }
  };
  const validateExperianOnSubmit = async (values_2: EditPaymentFormInterface) => {
    onClearError?.();
    if (values_2.addressType === 'id') {
      await save(values_2);
    } else {
      const addressValues = values_2?.address;
      setIsExperianLoading(true);
      const experianResponseValue = await validateExperianAddressLayout(addressValues);
      setIsExperianLoading(false);
      if (!experianResponseValue) {
        onAddressValidationApiError?.();
        return;
      }
      setExperianResponse(experianResponseValue);
    }
  };
  return <div>
            <Formik initialValues={paymentFormState} validate={validate} onSubmit={validateExperianOnSubmit}>
                {formikProps => <Form noValidate onSubmit={formikProps.handleSubmit} data-testid="billing-address-form">
                        <CardDetailsInputs cardData={editPaymentData} />

                        <ExperianModal experianResponse={experianResponse} saveSelectedAddress={async (formProps: unknown) => {
          await save(formProps as EditPaymentFormInterface);
        }} loading={isExperianLoading} setIsLoading={setIsExperianLoading} onVisibilityChange={onExperianModalVisibilityChange} />
                        <IsDefaultCheckbox disabled={isDefaultCheckDisabled} />
                        <BillingAddress onCancel={onCloseForm} defaultAddressId={defaultAddressId} isLoading={isExperianLoading || isSubmitting} handleErrorBanner={handleErrorBanner} addAddressIconSize={addAddressIconSize} />
                    </Form>}
            </Formik>
        </div>;
}